import type { Invoice } from '@dutchinvoice/types';
import type { SyncedInvoice, InvoiceMergeOptions } from '../types/invoice';

/**
 * Merges cloud and local invoices with consistent rules:
 * 1. Cloud invoices take precedence unless local has unsynced changes
 * 2. Uses invoiceNumber as the unique identifier
 * 3. Preserves sync status appropriately
 * 4. Special case: Always take payment status from cloud version
 */
export function mergeInvoices(
  cloudInvoices: Invoice[],
  localInvoices: SyncedInvoice[],
  options: InvoiceMergeOptions = {}
): SyncedInvoice[] {
  const { debug = false } = options;

  if (debug) {
    console.log('[Invoice Merge] Starting merge operation:', {
      localCount: localInvoices.length,
      cloudCount: cloudInvoices.length
    });
  }

  const merged = new Map<string, SyncedInvoice>();

  // Add all cloud invoices first (they take precedence)
  cloudInvoices.forEach(invoice => {
    if (debug) {
      console.log('[Invoice Merge] Processing cloud invoice:', {
        invoiceNumber: invoice.invoiceNumber,
        id: invoice.id,
        status: invoice.status,
        paymentStatus: invoice.paymentStatus
      });
    }

    merged.set(invoice.invoiceNumber, {
      ...invoice,
      synced: true // Mark cloud invoices as synced
    } as SyncedInvoice);
  });

  // Add or update with local invoices that have unsynced changes
  localInvoices.forEach(invoice => {
    if (debug) {
      console.log('[Invoice Merge] Processing local invoice:', {
        invoiceNumber: invoice.invoiceNumber,
        id: invoice.id,
        status: invoice.status,
        synced: invoice.synced,
        paymentStatus: invoice.paymentStatus
      });
    }

    const existingInvoice = merged.get(invoice.invoiceNumber);

    if (!existingInvoice) {
      // New local invoice, mark as unsynced
      if (debug) {
        console.log('[Invoice Merge] Adding new local invoice:', invoice.invoiceNumber);
      }
      merged.set(invoice.invoiceNumber, {
        ...invoice,
        synced: false
      });
    } else if (!invoice.synced) {
      // Local invoice has unsynced changes, but we need to preserve the paymentStatus from cloud
      if (debug) {
        console.log('[Invoice Merge] Merging changes - keeping cloud paymentStatus:', {
          invoiceNumber: invoice.invoiceNumber,
          localPaymentStatus: invoice.paymentStatus,
          cloudPaymentStatus: existingInvoice.paymentStatus
        });
      }
      merged.set(invoice.invoiceNumber, {
        ...invoice,
        // Always use cloud paymentStatus unless we're specifically changing it from the frontend
        paymentStatus: existingInvoice.paymentStatus,
        synced: false
      });
    }
  });

  const result = Array.from(merged.values());

  if (debug) {
    console.log('[Invoice Merge] Final merge result:', {
      totalCount: result.length
    });
  }

  return result;
}